import { AppShell, PageTitle } from "@/components/app-shell";
import { McpHelp } from "@/components/mcp-help";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SettingsSkeleton } from "@/components/mission-skeletons";

import {
  bindGithubRepo,
  createProjectInvite,
  getGithubAppStatus,
  getProjectGithub,
  getSettings,
  hwCollabEnabled,
  ingestGithubRepo,
  listGithubInstallations,
  listProjectInvites,
  patchSettings,
  revokeProjectInvite,
  unbindGithubRepo,
  type GithubAppStatus,
  type GithubBind,
  type GithubInstallation,
  type InviteCapability,
  type LibraryRef,
  type ProjectInvite,
  type ProjectAgentNote,
  type ProjectMember,
  type ProjectRow,
  type Role,
} from "@/lib/api";
import { projectMemberPrimaryLabel, projectMemberSecondaryLine } from "@/lib/project-member-label";
import {
  GITHUB_UNAVAILABLE_STATUS,
  githubBindFormVisible,
  githubSyncPhase,
} from "@/lib/github-sync-ui";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, type NavigateFunction } from "react-router-dom";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";

const TABS = ["members", "git", "lib", "mcp"] as const;
type SettingsTab = (typeof TABS)[number];

function asTab(raw: string | undefined): SettingsTab {
  if (raw && (TABS as readonly string[]).includes(raw)) return raw as SettingsTab;
  return "members";
}

export function SettingsPage() {
  const { orgSlug, projectSlug, tab: tabParam } = useParams();
  const nav = useNavigate();
  const tab = asTab(tabParam);
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [projectAgent, setProjectAgent] = useState<ProjectAgentNote | null>(null);
  const [libraries, setLibraries] = useState<LibraryRef[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [flag, setFlag] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [githubApp, setGithubApp] = useState<GithubAppStatus | null>(null);
  const [githubBind, setGithubBind] = useState<GithubBind | null>(null);
  const [installations, setInstallations] = useState<GithubInstallation[]>([]);
  const [installId, setInstallId] = useState("");
  const [repoInput, setRepoInput] = useState("");
  const [branchInput, setBranchInput] = useState("main");
  const [githubBusy, setGithubBusy] = useState(false);
  const [invites, setInvites] = useState<ProjectInvite[]>([]);
  const [inviteCapability, setInviteCapability] = useState<InviteCapability>("view");
  const [inviteEmail, setInviteEmail] = useState("");
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const body = await getSettings(orgSlug, projectSlug);
      if (!isCurrent()) return;
      setProject(body.project);
      setMembers(body.members ?? []);
      setProjectAgent(body.project_agent ?? null);
      setLibraries(body.libraries ?? []);
      setRole(body.role);
      setFlag(hwCollabEnabled(body.project.settings_json));
      try {
        const inviteBody = await listProjectInvites(orgSlug, projectSlug);
        if (!isCurrent()) return;
        setInvites(inviteBody.items ?? []);
      } catch {
        if (!isCurrent()) return;
        setInvites([]);
      }
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setErr(e instanceof Error ? e.message : "settings load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!orgSlug || !projectSlug) return;
    let cancelled = false;
    setGithubBind(null);
    setGithubApp(null);
    setInstallations([]);
    setInstallId("");
    setRepoInput("");
    setBranchInput("main");
    void (async () => {
      try {
        const body = await getProjectGithub(orgSlug, projectSlug);
        if (cancelled) return;
        setGithubApp(body.app);
        setGithubBind(body.bind);
        if (body.bind?.installation_id) setInstallId(body.bind.installation_id);
        if (body.bind?.repo) setRepoInput(body.bind.repo);
        if (body.bind?.default_branch) setBranchInput(body.bind.default_branch);
        if (body.app.configured) {
          try {
            const inst = await listGithubInstallations();
            if (!cancelled) setInstallations(inst.items ?? []);
          } catch {
            if (!cancelled) setInstallations([]);
          }
        } else {
          setInstallations([]);
        }
      } catch {
        if (cancelled) return;
        try {
          const status = await getGithubAppStatus();
          if (cancelled) return;
          setGithubApp(status);
        } catch {
          if (cancelled) return;
          setGithubApp(GITHUB_UNAVAILABLE_STATUS);
        }
        setGithubBind(null);
        setInstallations([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgSlug, projectSlug]);

  async function onFlag(next: boolean) {
    if (!orgSlug || !projectSlug) return;
    setBusy(true);
    try {
      const body = await patchSettings(orgSlug, projectSlug, { hw_collab_loop: next });
      setFlag(body.settings_json.hw_collab_loop === true);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "patch failed");
    } finally {
      setBusy(false);
    }
  }

  const libRows = libraries;

  const settingsBase = `/o/${orgSlug}/p/${projectSlug}/settings`;

  return (
    <AppShell title={<PageTitle kicker="owner write · others RO">Settings</PageTitle>}>
      {err && <p className="mb-3 text-sm text-destructive">{err}</p>}
      {loading ? (
        <SettingsSkeleton />
      ) : (
      <>

      <div className="mb-4 rounded-[4px] border border-border bg-card p-[18px]">
        <div className="mb-2.5 text-[12px] tracking-[0.04em] text-muted-foreground uppercase">MCP / Agent</div>
        <p className="mb-3 text-[13px] text-muted-foreground">
          MCP server bind lives here. Agents (Think dock, Circuit Mind canvas) is a project tab — this card is not that hub.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>MCP · unbound</Badge>
          <Badge variant="destructive">Agent · demo</Badge>
          <Button size="sm" variant="outline" onClick={() => nav(`${settingsBase}/mcp`)}>
            Configure
          </Button>
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(next) => {
          nav(next === "members" ? settingsBase : `${settingsBase}/${next}`);
        }}
      >
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="git">Git</TabsTrigger>
          <TabsTrigger value="lib">Libraries</TabsTrigger>
          <TabsTrigger value="mcp">MCP / Agent</TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="mt-4 space-y-4">
          <label className="flex items-center gap-2 text-sm">
            {/* Owner PATCH …/settings { hw_collab_loop } — no D1 poke required. */}
            <input
              type="checkbox"
              checked={flag}
              disabled={busy || role !== "owner"}
              onChange={(e) => void onFlag(e.target.checked)}
            />
            <span>
              hw_collab_loop{" "}
              <span className="text-xs text-muted-foreground">Hardware page uses D1 ingest for this project</span>
            </span>
          </label>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const secondary = projectMemberSecondaryLine(m);
                return (
                  <TableRow key={m.user_id}>
                    <TableCell>
                      <div className="text-sm font-medium">{projectMemberPrimaryLabel(m)}</div>
                      {secondary ? (
                        <div className="text-xs text-muted-foreground">{secondary}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Select defaultValue={m.role} disabled>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["owner", "editor", "viewer", "hardware", "firmware", "review", "guest"].map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
              {projectAgent ? (
                <TableRow data-testid="project-agent-note">
                  <TableCell>
                    <div className="text-sm font-medium">{projectAgent.title}</div>
                    <div className="text-xs text-muted-foreground">{projectAgent.description}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">service</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <div className="space-y-3 rounded-[4px] border border-border bg-card p-3" data-testid="project-invites">
            <div className="font-medium">Invites</div>
            <p className="text-xs text-muted-foreground">
              edit → editor, view → viewer. Token is shown once; D1 stores the SHA-256 hash. Redeem at{" "}
              <Link className="underline" to="/invite">
                /invite
              </Link>
              .
            </p>
            {(role === "owner" || role === "editor") && (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!orgSlug || !projectSlug) return;
                  setBusy(true);
                  void createProjectInvite(orgSlug, projectSlug, {
                    capability: inviteCapability,
                    email: inviteEmail.trim() || undefined,
                  })
                    .then((invite) => {
                      setIssuedToken(invite.token ?? null);
                      setInvites((rows) => [invite, ...rows]);
                      setErr(null);
                    })
                    .catch((e) => setErr(e instanceof Error ? e.message : "invite failed"))
                    .finally(() => setBusy(false));
                }}
              >
                <div>
                  <Label>Capability</Label>
                  <Select
                    value={inviteCapability}
                    onValueChange={(value) => setInviteCapability(value as InviteCapability)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="edit">edit</SelectItem>
                      <SelectItem value="view">view</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[12rem] flex-1">
                  <Label htmlFor="invite-email">Email (optional)</Label>
                  <Input
                    id="invite-email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@pcbx.ai"
                  />
                </div>
                <Button type="submit" size="sm" disabled={busy}>
                  Create invite
                </Button>
              </form>
            )}
            {issuedToken && (
              <p className="break-all font-mono text-xs" data-testid="issued-invite-token">
                {issuedToken}
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Capability</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => {
                  const status = invite.revoked_at
                    ? "revoked"
                    : invite.redeemed_at
                      ? "redeemed"
                      : "pending";
                  return (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.capability}</TableCell>
                      <TableCell>{invite.role}</TableCell>
                      <TableCell className="font-mono text-xs">{invite.expires_at}</TableCell>
                      <TableCell>{status}</TableCell>
                      <TableCell>
                        {(role === "owner" || role === "editor") && status === "pending" && orgSlug && projectSlug && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => {
                              setBusy(true);
                              void revokeProjectInvite(orgSlug, projectSlug, invite.id)
                                .then((body) => {
                                  setInvites((rows) =>
                                    rows.map((row) => (row.id === body.invite.id ? body.invite : row)),
                                  );
                                })
                                .catch((e) => setErr(e instanceof Error ? e.message : "revoke failed"))
                                .finally(() => setBusy(false));
                            }}
                          >
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {invites.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground">
                      No invites yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="git" className="mt-4 max-w-xl space-y-4">
          <div>
            <Label>Name</Label>
            <Input className="font-mono" defaultValue={project?.name || projectSlug || ""} readOnly />
          </div>
          <div
            className="rounded-[4px] border border-border bg-card p-3 text-[13px]"
            data-testid="platform-hosting"
          >
            <div className="mb-1 font-medium">Platform hosting</div>
            <p className="mb-2 text-muted-foreground">
              Source of truth is hosted hardware revisions on this platform — KiCad zip upload, R2 artifacts, and
              libgit2 bundles. GitHub is not required and is not the host.
            </p>
            <p className="mb-3 font-mono text-xs text-muted-foreground">
              {flag ? "hosted revisions on (hw_collab_loop)" : "hosted revisions off — enable on Members"}
            </p>
            <Button size="sm" asChild>
              <Link to={`/o/${orgSlug}/p/${projectSlug}/files#upload`}>Upload KiCad zip</Link>
            </Button>
          </div>
          <GithubSyncCard
            githubApp={githubApp}
            githubBind={githubBind}
            githubBusy={githubBusy}
            installations={installations}
            installId={installId}
            repoInput={repoInput}
            branchInput={branchInput}
            role={role}
            setInstallId={setInstallId}
            setRepoInput={setRepoInput}
            setBranchInput={setBranchInput}
            setGithubBind={setGithubBind}
            setGithubBusy={setGithubBusy}
            setErr={setErr}
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            nav={nav}
          />
          <div>
            <Label>default_git_remote</Label>
            <Input
              className="font-mono"
              value={githubBind?.default_git_remote || project?.default_git_remote || ""}
              readOnly
            />
          </div>
        </TabsContent>
        <TabsContent value="lib" className="mt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>library_id</TableHead>
                <TableHead>kind</TableHead>
                <TableHead>git</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {libRows.map((l) => (
                <TableRow key={`${l.library_id}:${l.ref_kind}`}>
                  <TableCell className="font-mono text-xs">{l.library_id}</TableCell>
                  <TableCell>{l.ref_kind}</TableCell>
                  <TableCell className="font-mono text-xs">{l.git_url || l.git_oid || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="mcp" className="mt-4">
          <McpHelp />
        </TabsContent>
      </Tabs>
      </>
      )}
    </AppShell>
  );
}

type GithubSyncCardProps = {
  githubApp: GithubAppStatus | null;
  githubBind: GithubBind | null;
  githubBusy: boolean;
  installations: GithubInstallation[];
  installId: string;
  repoInput: string;
  branchInput: string;
  role: Role | null;
  setInstallId: (v: string) => void;
  setRepoInput: (v: string) => void;
  setBranchInput: (v: string) => void;
  setGithubBind: (v: GithubBind | null) => void;
  setGithubBusy: (v: boolean) => void;
  setErr: (v: string | null) => void;
  orgSlug: string | undefined;
  projectSlug: string | undefined;
  nav: NavigateFunction;
};

function GithubSyncCard({
  githubApp,
  githubBind,
  githubBusy,
  installations,
  installId,
  repoInput,
  branchInput,
  role,
  setInstallId,
  setRepoInput,
  setBranchInput,
  setGithubBind,
  setGithubBusy,
  setErr,
  orgSlug,
  projectSlug,
  nav,
}: GithubSyncCardProps) {
  const phase = githubSyncPhase(githubApp);
  const showBind = githubBindFormVisible(phase);

  return (
    <div
      className="rounded-[4px] border border-dashed border-border bg-muted/20 p-3 text-[13px]"
      data-testid="github-sync-optional"
    >
      <div className="mb-1 font-medium">Optional GitHub sync / import</div>
      <p className="mb-2 text-muted-foreground">
        Optional entry to import or sync a repo. Not the source of truth. Separate from user GitHub OAuth login.
        Installation tokens stay server-side; the API never returns PATs.
      </p>
      {phase === "unbound" ? (
        <p className="mb-2 text-muted-foreground" data-testid="github-sync-unbound">
          GitHub App is not bound on this deployment. That is expected — hosting does not require it. Upload a KiCad zip
          on Hardware instead.
        </p>
      ) : null}
      {showBind ? (
        <>
          <div className="mb-1 font-medium">GitHub App (repo bind)</div>
          <p className="mb-2 font-mono text-xs">
            configured
            {githubApp?.install_url ? (
              <>
                {" · "}
                <a className="underline" href={githubApp.install_url} target="_blank" rel="noreferrer">
                  install app
                </a>
              </>
            ) : null}
          </p>
          <div className="mb-2 space-y-2">
            <div>
              <Label htmlFor="gh-install-id">installation_id</Label>
              {installations.length > 0 ? (
                <Select
                  value={installId || undefined}
                  onValueChange={setInstallId}
                  disabled={role !== "owner" || githubBusy}
                >
                  <SelectTrigger id="gh-install-id">
                    <SelectValue placeholder="Select installation" />
                  </SelectTrigger>
                  <SelectContent>
                    {installations.map((i) => (
                      <SelectItem key={i.installation_id} value={i.installation_id}>
                        {i.account_login} ({i.installation_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="gh-install-id"
                  className="font-mono"
                  value={installId}
                  disabled={role !== "owner" || githubBusy}
                  onChange={(e) => setInstallId(e.target.value)}
                  placeholder="12345678"
                />
              )}
            </div>
            <div>
              <Label htmlFor="gh-repo">repo (owner/name)</Label>
              <Input
                id="gh-repo"
                className="font-mono"
                value={repoInput}
                disabled={role !== "owner" || githubBusy}
                onChange={(e) => setRepoInput(e.target.value)}
                placeholder="acme/board"
              />
            </div>
            <div>
              <Label htmlFor="gh-branch">default_branch</Label>
              <Input
                id="gh-branch"
                className="font-mono"
                value={branchInput}
                disabled={role !== "owner" || githubBusy}
                onChange={(e) => setBranchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={role !== "owner" || githubBusy || !installId || !repoInput}
              onClick={() => {
                if (!orgSlug || !projectSlug) return;
                setGithubBusy(true);
                void bindGithubRepo(orgSlug, projectSlug, {
                  installation_id: installId,
                  repo: repoInput,
                  default_branch: branchInput || "main",
                })
                  .then((r) => {
                    setGithubBind(r.bind);
                    setErr(null);
                  })
                  .catch((e) => setErr(e instanceof Error ? e.message : "bind failed"))
                  .finally(() => setGithubBusy(false));
              }}
            >
              Bind repo
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={role !== "owner" || githubBusy || !githubBind?.installation_id}
              onClick={() => {
                if (!orgSlug || !projectSlug) return;
                setGithubBusy(true);
                void unbindGithubRepo(orgSlug, projectSlug)
                  .then(() => {
                    setGithubBind({
                      forge: null,
                      repo: null,
                      installation_id: null,
                      default_branch: "main",
                      default_git_remote: null,
                    });
                    setInstallId("");
                    setRepoInput("");
                    setErr(null);
                  })
                  .catch((e) => setErr(e instanceof Error ? e.message : "unbind failed"))
                  .finally(() => setGithubBusy(false));
              }}
            >
              Unbind
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={githubBusy || !githubBind?.installation_id || (role !== "owner" && role !== "hardware" && role !== "editor")}
              onClick={() => {
                if (!orgSlug || !projectSlug) return;
                setGithubBusy(true);
                void ingestGithubRepo(orgSlug, projectSlug, { ref: branchInput || "main" })
                  .then((r) => {
                    setErr(null);
                    nav(`/o/${orgSlug}/p/${projectSlug}/jobs`);
                    void r;
                  })
                  .catch((e) => setErr(e instanceof Error ? e.message : "ingest failed"))
                  .finally(() => setGithubBusy(false));
              }}
            >
              Ingest from GitHub
            </Button>
          </div>
          {githubBind?.repo ? (
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              bound {githubBind.repo} @ {githubBind.default_branch} (installation {githubBind.installation_id})
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
